import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  Layers,
  ArrowRight,
  User,
  Clock,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Monitor,
} from 'lucide-react'

/**
 * ProjectLabHome — Participant Landing Page
 *
 * This is the first meaningful screen a Project Lab participant sees
 * after login. It immediately communicates their project context:
 *
 *   Project name → Current stage → Next action → Mentor → Final deliverable
 *
 * Not a generic dashboard. An editorial workspace view.
 */

function StageTimeline({ currentIndex, stages }) {
  return (
    <div className="space-y-0">
      {(stages || []).map((stage) => {
        const idx = stage.stage_index
        const label = stage.title || `Stage ${idx}`
        const status = stage?.status || 'locked'
        const isCurrent = idx === currentIndex
        const isCompleted = status === 'approved' || status === 'completed'
        const isSubmitted = status === 'submitted'
        const isRevision = status === 'needs_revision'

        return (
          <div
            key={stage.stage_id || idx}
            className={`flex items-start gap-4 py-3 border-l-2 pl-5 transition-colors ${
              isCurrent
                ? 'border-cinema-gold'
                : isCompleted
                ? 'border-emerald-500/40'
                : 'border-white/8'
            }`}
          >
            {/* Stage number */}
            <span
              className={`w-7 h-7 flex items-center justify-center text-[11px] font-mono font-bold shrink-0 ${
                isCompleted
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                  : isCurrent
                  ? 'bg-cinema-gold/15 text-cinema-gold border border-cinema-gold/40'
                  : 'bg-white/[0.03] text-cinema-muted/40 border border-white/8'
              }`}
            >
              {isCompleted ? <CheckCircle2 size={13} /> : String(idx).padStart(2, '0')}
            </span>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-medium ${
                    isCurrent
                      ? 'text-white'
                      : isCompleted
                      ? 'text-emerald-400/80'
                      : 'text-cinema-muted/50'
                  }`}
                >
                  {label}
                </span>
                {isCurrent && (
                  <span className="text-[10px] uppercase tracking-wider text-cinema-gold font-medium">
                    Current
                  </span>
                )}
                {isSubmitted && (
                  <span className="text-[10px] uppercase tracking-wider text-cyan-400 font-medium">
                    Submitted
                  </span>
                )}
                {isRevision && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-400 font-medium">
                    Needs Revision
                  </span>
                )}
              </div>
              {isCurrent && stage?.guide?.objective && (
                <p className="text-xs text-cinema-muted mt-1 leading-relaxed line-clamp-2">
                  {stage.guide.objective}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function ProjectLabHome({ enrollments = [] }) {
  const navigate = useNavigate()
  const [projectData, setProjectData] = useState(null)
  const [projectStatus, setProjectStatus] = useState('loading') // loading | ready | error

  // Pick the primary enrollment (first active/in_progress, or first overall)
  const primary = enrollments.find(
    (e) => e.status === 'active' || e.status === 'in_progress'
  ) || enrollments[0]

  useEffect(() => {
    if (!primary) return

    let cancelled = false
    setProjectStatus('loading')
    api.get(`/project-lab/student/projects/${primary.id}`)
      .then((res) => {
        if (cancelled) return
        setProjectData(res.data)
        setProjectStatus('ready')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[ProjectLabHome] Failed to load project:', err)
        setProjectStatus('error')
      })
    return () => { cancelled = true }
  }, [primary?.id])

  // ── No enrollment state ──────────────────────────────────────────────────
  if (enrollments.length === 0) {
    return (
      <div className="space-y-10 max-w-2xl">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
            Project Lab
          </span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light">
            No active project
          </h1>
          <p className="text-sm text-cinema-muted mt-3 leading-relaxed max-w-md">
            You are not currently enrolled in a Project Lab project.
            Explore our current project to apply.
          </p>
        </div>

        <Link
          to="/project-lab"
          className="inline-flex items-center gap-2 px-6 py-3 bg-cinema-gold text-black font-medium text-sm hover:bg-yellow-400 transition-colors"
        >
          Explore Project Lab
          <ArrowRight size={16} />
        </Link>
      </div>
    )
  }

  if (projectStatus === 'loading') {
    return <LoadingSpinner label="Loading your project" />
  }

  const enrollment = projectData?.enrollment
  const project = projectData?.project
  const mentor = projectData?.mentor
  const stages = projectData?.stages || []
  const currentStageIndex = enrollment?.current_stage_index || primary.current_stage_index || 1
  const activeStage = stages.find((s) => s.stage_index === currentStageIndex) || stages[0]
  const isCompleted = enrollment?.status === 'completed'
  const completedStages = stages.filter((s) => s.status === 'approved' || s.status === 'completed').length
  const progressPct = stages.length ? Math.round((completedStages / stages.length) * 100) : 0

  // Determine next action text
  let nextAction = 'Follow stage guidance and submit requirements.'
  if (projectStatus === 'error') {
    nextAction = 'Your project details could not be loaded right now. Open the project workspace or try again shortly.'
  } else if (activeStage?.status === 'submitted') {
    nextAction = 'Your work has been submitted. Awaiting mentor review.'
  } else if (activeStage?.status === 'needs_revision') {
    nextAction = 'Mentor has requested revisions. Review feedback and resubmit.'
  } else if (activeStage?.status === 'approved' || activeStage?.status === 'completed') {
    if (currentStageIndex < 8) {
      nextAction = `Stage ${currentStageIndex} approved. Proceed to the next stage.`
    } else {
      nextAction = 'All stages completed. Your project is finished.'
    }
  } else if (activeStage?.guide?.objective) {
    nextAction = activeStage.guide.objective
  }

  return (
    <div className="space-y-0">
      {/* ── Project Identity ─────────────────────────────────────────── */}
      <div className="pb-8 border-b border-white/8">
        <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
          Project Lab
        </span>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white font-light tracking-tight">
          {primary.project_title || project?.title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-cinema-muted font-medium">
          {primary.discipline && <span className="capitalize">{primary.discipline}</span>}
          {primary.discipline && primary.duration && <span className="text-white/15">·</span>}
          {primary.duration && <span>{primary.duration}</span>}
          {primary.mode && (
            <>
              <span className="text-white/15">·</span>
              <span className="capitalize">{primary.mode} Mode</span>
            </>
          )}
          {isCompleted && (
            <>
              <span className="text-white/15">·</span>
              <span className="text-emerald-400">Completed</span>
            </>
          )}
        </div>
      </div>

      {/* ── Two-column editorial layout ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-12 pt-8">

        {/* ─── LEFT / PRIMARY ────────────────────────────────────────── */}
        <div className="lg:col-span-7 space-y-8">

          {/* Current Stage + Next Action */}
          <div className="space-y-4">
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-gold font-medium block mb-2">
                Current Stage
              </span>
              <h2 className="font-display text-xl sm:text-2xl text-white font-medium">
                {String(currentStageIndex).padStart(2, '0')} — {activeStage?.title || `Stage ${currentStageIndex}`}
              </h2>
              <span
                className={`inline-block mt-2 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 border ${
                  activeStage?.status === 'approved' || activeStage?.status === 'completed'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                    : activeStage?.status === 'submitted'
                    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                    : activeStage?.status === 'needs_revision'
                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                    : 'bg-white/5 text-cinema-muted border-white/10'
                }`}
              >
                {activeStage?.status?.replace('_', ' ') || (projectStatus === 'error' ? 'Unavailable' : 'Not started')}
              </span>
            </div>

            <div className="border-l-2 border-cinema-gold/40 pl-5 py-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-1">
                Next Action
              </span>
              <p className="text-sm text-cinema-soft leading-relaxed">
                {nextAction}
              </p>
            </div>

            <Link
              to={`/student/project-lab/${primary.id}`}
              className="inline-flex items-center gap-2 px-5 py-3 bg-cinema-gold text-black font-medium text-sm hover:bg-yellow-400 transition-colors"
            >
              Open Project Workspace
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Stage Timeline */}
          <div className="pt-8 border-t border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-4">
              Workflow
            </span>
            <StageTimeline currentIndex={currentStageIndex} stages={stages} />
          </div>
        </div>

        {/* ─── RIGHT / SUPPORTING ─────────────────────────────────────── */}
        <div className="lg:col-span-5 space-y-0 mt-8 lg:mt-0 lg:border-l lg:border-white/8 lg:pl-10">

          {/* Progress */}
          <div className="pb-6 border-b border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
              Progress
            </span>
            <div className="flex items-end gap-3 mb-2">
              <span className="font-display text-3xl text-white font-light">{progressPct}%</span>
              <span className="text-xs text-cinema-muted font-medium pb-1">
                {completedStages} of {stages.length} stages
              </span>
            </div>
            <div className="w-full h-1.5 bg-white/5">
              <div
                className="h-1.5 bg-cinema-gold transition-all duration-700"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Final Deliverable */}
          <div className="py-6 border-b border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-2">
              Final Deliverable
            </span>
            <p className="text-sm text-white leading-relaxed">
              {primary.final_deliverable || project?.final_deliverable || '—'}
            </p>
          </div>

          {/* Mentor */}
          <div className="py-6 border-b border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
              Mentor
            </span>
            {mentor ? (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-cinema-gold/15 border border-cinema-gold/30 flex items-center justify-center text-cinema-gold text-xs font-bold">
                  {mentor.first_name?.[0]}{mentor.last_name?.[0]}
                </div>
                <div>
                  <span className="text-sm text-white font-medium block">
                    {mentor.first_name} {mentor.last_name}
                  </span>
                  {mentor.title && (
                    <span className="text-[11px] text-cinema-muted">{mentor.title}</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-cinema-muted italic">Not yet assigned</p>
            )}
          </div>

          {/* Project Specifications */}
          <div className="py-6 border-b border-white/8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
              Project Specifications
            </span>
            <dl className="space-y-2.5">
              {[
                { label: 'Discipline', value: primary.discipline },
                { label: 'Level', value: primary.level },
                { label: 'Mode', value: primary.mode },
                { label: 'Duration', value: primary.duration },
              ].filter(d => d.value).map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <dt className="text-xs text-cinema-muted">{label}</dt>
                  <dd className="text-xs text-white font-medium capitalize">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Digital Lab Entry */}
          <div className="pt-6">
            <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
              Workspace
            </span>
            <Link
              to={enrollment?.digital_lab_project_id
                ? `/student/digital-lab/projects/${enrollment.digital_lab_project_id}`
                : `/student/project-lab/${primary.id}`}
              className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 text-sm text-cinema-soft hover:text-white hover:border-cinema-gold/30 transition-all group"
            >
              <div className="flex items-center gap-2">
                <Monitor size={14} className="text-cinema-muted group-hover:text-cinema-gold transition-colors" />
                <span>Open Digital Lab</span>
              </div>
              <ChevronRight size={14} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
            </Link>
          </div>

          {/* Completed Projects */}
          {enrollments.filter(e => e.status === 'completed').length > 0 && (
            <div className="pt-6 mt-6 border-t border-white/8">
              <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
                Completed Projects
              </span>
              <div className="space-y-2">
                {enrollments.filter(e => e.status === 'completed').map((e) => (
                  <Link
                    key={e.id}
                    to={`/student/project-lab/${e.id}`}
                    className="flex items-center justify-between gap-3 border border-white/8 px-4 py-3 text-sm text-cinema-muted hover:text-white hover:border-white/15 transition-all group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 size={13} className="text-emerald-400/60 shrink-0" />
                      <span className="truncate">{e.project_title}</span>
                    </div>
                    <ChevronRight size={14} className="text-cinema-muted/40 shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
